import { useCallback, useMemo } from 'react';

import { useIntl } from 'react-intl';

import { ListCardGroup } from 'components/list-card-group';
import { ListCardGroupVariant } from 'components/list-card-group/types';
import { useUnifiedMenuContext } from 'state/unified-menu';
import { useMenuQueryOptions } from 'state/unified-menu/use-menu-query-options';
import { hasOptionEntries, mapAvailableOptions } from 'state/unified-menu/utils';

import { useProductWizardContext } from '../../product-wizard';
import { DEFAULT_SLOT_KEY, SlotSelections } from '../../product-wizard/types';
import { useProductWizardFlow } from '../../product-wizard/use-product-wizard-flow';
import { Modifier } from '../modifier/modifier';
import { IModifier, IModifierData } from '../modifier/types';
import { getValidModifiersForSelection, mapModifier } from '../modifier/utils';

import { IModifierGroup, ModifierGroup } from './product-customization-modifier-group';
import { GroupsCustomizationModifiersContainer, ModifiersCustomizationContainer } from './styled';
import { ComboSlotType, SelectModifierFn } from './types';
import { getComboSlotType, groupModifiersByDisplayName } from './utils';

export const ProductCustomizationModifiers = () => {
  const { posServiceMode } = useMenuQueryOptions();
  const { indexedMenu } = useUnifiedMenuContext();
  const { slotsSelections, selectModifierMultiplier } = useProductWizardContext();
  const { currentFlowStep } = useProductWizardFlow();

  const slotKey = currentFlowStep.slotKey || DEFAULT_SLOT_KEY;
  const slotSelections = slotsSelections[slotKey];
  const selectedItemId = slotSelections?.itemId;
  const shouldGroupModifiers =
    slotKey === DEFAULT_SLOT_KEY ||
    getComboSlotType(indexedMenu, slotKey) === ComboSlotType.PICKER_ASPECT;

  const modifiers = useMemo(() => {
    if (!selectedItemId) {
      return [];
    }

    const selectedItem = indexedMenu.get(selectedItemId);
    if (!hasOptionEntries(indexedMenu, posServiceMode, selectedItem)) {
      return [];
    }

    const modifiers = mapAvailableOptions(
      indexedMenu,
      posServiceMode,
      selectedItem.options.entries,
      ({ entryId, quantity }) =>
        mapModifier(
          indexedMenu,
          entryId,
          selectedItem.options.entries.length,
          posServiceMode,
          quantity
        )
    );

    const validModifiers = getValidModifiersForSelection(modifiers);
    return validModifiers;
  }, [indexedMenu, posServiceMode, selectedItemId]);

  const onModifierSelectionChange = useCallback<SelectModifierFn>(
    (selections, modifier) => {
      if (selectedItemId) {
        selectModifierMultiplier(slotKey, selectedItemId, modifier.id, selections);
      }
    },
    [selectModifierMultiplier, selectedItemId, slotKey]
  );

  if (modifiers.length === 0) {
    return null;
  }

  const Component = shouldGroupModifiers
    ? GroupsCustomizationModifiers
    : FlatCustomizationModifiers;

  return (
    <Component
      modifiers={modifiers}
      slotSelections={slotSelections}
      onModifierSelectionChange={onModifierSelectionChange}
    />
  );
};

export const GroupsCustomizationModifiers = ({
  modifiers,
  slotSelections,
  onModifierSelectionChange,
}: {
  modifiers: IModifier[];
  slotSelections: SlotSelections | undefined;
  onModifierSelectionChange: SelectModifierFn;
}) => {
  const modifierGroupsProps = useMemo<IModifierGroup[]>(() => {
    const modifierSelections = slotSelections?.modifiers ?? {};
    const groups = groupModifiersByDisplayName(modifiers, modifierSelections);
    return groups;
  }, [modifiers, slotSelections?.modifiers]);

  return (
    <GroupsCustomizationModifiersContainer>
      {modifierGroupsProps.map(modifierGroupProps =>
        modifierGroupProps.modifiers.length ? (
          <ModifierGroup
            {...modifierGroupProps}
            key={modifierGroupProps.key}
            onModifierSelectionChange={onModifierSelectionChange}
          />
        ) : null
      )}
    </GroupsCustomizationModifiersContainer>
  );
};

export const FlatCustomizationModifiers = ({
  modifiers,
  slotSelections,
  onModifierSelectionChange,
}: {
  modifiers: IModifier[];
  slotSelections: SlotSelections | undefined;
  onModifierSelectionChange: SelectModifierFn;
}) => {
  const { formatMessage } = useIntl();
  const modifiersData = useMemo<IModifierData[]>(() => {
    const modifierSelections = slotSelections?.modifiers ?? {};
    return modifiers.map(modifier => ({
      modifier,
      selections: modifierSelections[modifier.id] || {},
    }));
  }, [modifiers, slotSelections?.modifiers]);

  return (
    <ModifiersCustomizationContainer>
      <ListCardGroup
        isFullContainer
        items={modifiersData}
        variant={ListCardGroupVariant.SINGLE}
        header={formatMessage({ id: 'customizations' })}
        renderItem={({ item: modifierProps }: { item: IModifierData }) => {
          return (
            <Modifier
              key={`modifier-${modifierProps.modifier.id}`}
              {...modifierProps}
              onSelectionsChange={selections => {
                onModifierSelectionChange(selections, modifierProps.modifier);
              }}
            />
          );
        }}
      />
    </ModifiersCustomizationContainer>
  );
};
